import { ApiError } from '../../services/apiClient';
import type { ApiMeResponse } from '../../services/teamsSetupApi';
import { teamsSetupApi } from '../../services/teamsSetupApi';

export const DEFAULT_BOARD_TITLE = 'Dotstorming Board';

export type TeamsContextLike = Record<string, unknown>;

export type TeamsAuthenticationLike = {
  getAuthToken?: (request?: {
    resources?: string[];
    silent?: boolean;
    successCallback?: (token: string) => void;
    failureCallback?: (reason?: string) => void;
  }) => Promise<string> | void;
  authenticate?: (request: {
    url: string;
    width?: number;
    height?: number;
    successCallback?: (result: string) => void;
    failureCallback?: (reason?: string) => void;
  }) => Promise<string> | void;
};

const get = (obj: unknown, key: string): unknown => {
  if (!obj || typeof obj !== 'object') return null;
  return (obj as Record<string, unknown>)[key];
};

const getStr = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const bestEffortEmailFromContext = (ctx: TeamsContextLike) => {
  const user = get(ctx, 'user');
  return (
    getStr(get(user, 'loginHint')) ??
    getStr(get(user, 'userPrincipalName')) ??
    getStr(get(ctx, 'loginHint')) ??
    getStr(get(ctx, 'userPrincipalName')) ??
    null
  );
};

export const bestEffortNameFromContext = (ctx: TeamsContextLike) => {
  const user = get(ctx, 'user');
  return (
    getStr(get(user, 'displayName')) ??
    getStr(get(ctx, 'userDisplayName')) ??
    bestEffortEmailFromContext(ctx) ??
    null
  );
};

const getTenantIdFromContext = (ctx: TeamsContextLike) => {
  const user = get(ctx, 'user');
  const tenant = get(user, 'tenant');
  return getStr(get(tenant, 'id')) ?? getStr(get(ctx, 'tid')) ?? null;
};

const getTeamsAuthToken = async (authentication: TeamsAuthenticationLike): Promise<string> => {
  const getAuthToken = authentication.getAuthToken;
  if (!getAuthToken) {
    throw new Error('Teams authentication API is unavailable.');
  }

  return new Promise<string>((resolve, reject) => {
    let settled = false;

    const request = {
      silent: false,
      successCallback: (token: string) => {
        settled = true;
        resolve(token);
      },
      failureCallback: (reason?: string) => {
        settled = true;
        reject(new Error(reason ?? 'Teams SSO token request failed.'));
      },
    };

    try {
      const maybePromise = getAuthToken(request);
      if (maybePromise && typeof (maybePromise as Promise<string>).then === 'function') {
        (maybePromise as Promise<string>).then(resolve).catch(reject);
      } else {
        window.setTimeout(() => {
          if (!settled) reject(new Error('Teams token request timed out.'));
        }, 15000);
      }
    } catch (error) {
      reject(error instanceof Error ? error : new Error('Unable to request Teams SSO token.'));
    }
  });
};

const needsInteractiveConsent = (error: unknown) => {
  if (error instanceof ApiError) {
    if (error.status === 403) return true;
    const msg = (error.message || '').toLowerCase();
    return msg.includes('consent_required') || msg.includes('graph_forbidden');
  }

  const msg = error instanceof Error ? error.message.toLowerCase() : '';
  return msg.includes('consent_required') || msg.includes('graph_forbidden');
};

const runInteractiveConsent = async (
  authentication: TeamsAuthenticationLike,
  tenantId?: string | null
) => {
  const authenticate = authentication.authenticate;
  if (!authenticate) {
    throw new Error('Teams interactive authentication API is unavailable.');
  }

  const state =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2, 10);

  const url = new URL(teamsSetupApi.getOauthStartUrl());
  url.searchParams.set('state', state);
  if (tenantId) {
    url.searchParams.set('tenantId', tenantId);
  }

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const request = {
      url: url.toString(),
      width: 600,
      height: 535,
      successCallback: () => {
        settled = true;
        resolve();
      },
      failureCallback: (reason?: string) => {
        settled = true;
        reject(new Error(reason ?? 'Interactive consent failed.'));
      },
    };

    try {
      const maybePromise = authenticate(request);
      if (maybePromise && typeof (maybePromise as Promise<string>).then === 'function') {
        (maybePromise as Promise<string>)
          .then(() => {
            if (!settled) resolve();
          })
          .catch((error) =>
            reject(error instanceof Error ? error : new Error('Interactive consent failed.'))
          );
      }
    } catch (error) {
      reject(error instanceof Error ? error : new Error('Interactive consent failed.'));
    }
  });
};

export const buildFetchErrorMessage = (error: unknown, url: string) => {
  const generic = error instanceof Error ? error.message : 'Failed to fetch Teams profile.';
  if (generic !== 'Failed to fetch') return generic;

  const isMixedContent =
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    url.startsWith('http://');

  if (isMixedContent) {
    return `Network request blocked. This page is HTTPS but API URL is HTTP (${url}). Configure VITE_API_BASE_URL to an HTTPS API origin for Teams tabs.`;
  }

  return `Network request failed for ${url}. Verify the API is reachable from the Teams host and allowed in the Teams app manifest validDomains.`;
};

export const resolveTeamsIdentity = async (input: {
  authentication?: TeamsAuthenticationLike;
  context: TeamsContextLike;
}): Promise<{
  apiMe: ApiMeResponse | null;
  appAuthToken: string | null;
  participantId: string | null;
  displayName: string | null;
  defaultBoardTitle: string;
}> => {
  const { authentication, context } = input;
  if (!authentication) {
    throw new Error('Teams authentication API is unavailable.');
  }

  const token = await getTeamsAuthToken(authentication);
  let authenticatedIdentity: ApiMeResponse | null = null;

  try {
    const payload = await teamsSetupApi.getMe(token);
    authenticatedIdentity = payload;
  } catch (error) {
    if (!needsInteractiveConsent(error)) {
      throw error;
    }

    await runInteractiveConsent(authentication, getTenantIdFromContext(context));
    authenticatedIdentity = await teamsSetupApi.getMe(token);
  }

  const displayName = authenticatedIdentity?.displayName ?? bestEffortNameFromContext(context);
  const defaultBoardTitle =
    (displayName && displayName.trim().length > 0 ? `${displayName}'s Board` : null) ??
    DEFAULT_BOARD_TITLE;

  return {
    apiMe: authenticatedIdentity,
    appAuthToken: authenticatedIdentity.appAuthToken ?? null,
    participantId: authenticatedIdentity.user?.id ?? null,
    displayName,
    defaultBoardTitle,
  };
};
